import { useMemo } from "react";
import { useEnvironments } from "../../api/queries";
import type { Environment } from "../../api/types";

/**
 * A tenant's environments keyed by id — the lookup every environment-facet
 * surface needs to resolve a `Node`/`Host`'s `environmentId` to its policy
 * CLASS for `EnvironmentBadge` (Host/Deploy UX Phase 4, #526): `Node`/`Host`
 * carry `environmentId`/`environmentName` directly (see types.ts) but not
 * `class` — only `Environment` itself does. Bounded the same way every small
 * reference list in this codebase is (`limit: 100`, the `nodesById`
 * precedent in `PlacementsSection`) — realistic environment counts stay well
 * under that through Beta Phase 1.
 *
 * Lifted here (charter A-12) rather than left to drift as four separate
 * inline `useMemo`s, since this PR introduces all four call sites at once
 * (`PlacementsSection`, `DeploymentDetailModal`, `HostsListPage`,
 * `HostDetailPage`) — the "recurs a third time" trigger, met on day one.
 * Absent while the list is still loading (or on error) — callers pass the
 * resulting `undefined` class straight to `EnvironmentBadge`, which renders
 * a neutral pill rather than blocking on this fetch.
 */
export function useEnvironmentsById(): Map<string, Environment> {
  const { data } = useEnvironments({ limit: 100 });
  return useMemo(() => {
    const map = new Map<string, Environment>();
    for (const env of data?.items ?? []) map.set(env.id, env);
    return map;
  }, [data]);
}
